import map from "lodash/map";

const DEFAULT_ZOOM_HOURS = 7 * 24;

const SIDE_PANEL_WIDTHS = 160;
const EXPANDED_SIDE_PANEL_WIDTHS = 564;

function sidePanelWidth(expanded) {
  return expanded ? EXPANDED_SIDE_PANEL_WIDTHS : SIDE_PANEL_WIDTHS;
}

const MIN_TICK_WIDTH = 50;
function buildTimeResolution() {
  return {
    increment: 5,
    unit: "minute",
  };
}

function buildTopRowDayHeader(zoomable = true) {
  return {
    dateFormat: "DDD | ddd MMM D",
    headerCellCls: zoomable ? "bryntumDateHeaderStyle zoomableDateHeader" : "bryntumDateHeaderStyle",
    unit: "day",
  };
}

function buildSecondRowDayHeader() {
  return {
    dateFormat: "ddd MMM D",
    headerCellCls: "bryntumTimeHeaderStyle",
    unit: "day",
  };
}

function buildMinuteRowHeader({ increment = 30 } = {}) {
  return {
    dateFormat: "h:mm A",
    headerCellCls: "bryntumTimeHeaderStyle",
    increment,
    unit: "minute",
  };
}

function buildHourRowHeader({ increment = 1 } = {}) {
  return {
    dateFormat: "h A",
    headerCellCls: "bryntumTimeHeaderStyle",
    increment,
    unit: "hour",
  };
}

function getTickWidth(ticks, expanded) {
  return Math.max((window.innerWidth - sidePanelWidth(expanded)) / ticks, MIN_TICK_WIDTH);
}

const defaultPresetProperties = (ticks) => ({
  defaultSpan: 7,
  displayDateFormat: "ddd HH:mm",
  tickHeight: 50,
  tickWidth: getTickWidth(ticks, false),
  timeResolution: buildTimeResolution(),
});

const ZoomLevels = [
  {
    hours: 2,
    index: 0,
    label: "2 hr",
    preset: {
      ...defaultPresetProperties(12),
      headers: [buildTopRowDayHeader(false), buildMinuteRowHeader({ increment: 10 })],
      id: "2hr",
      level: 0,
      ticks: 12,
    },
    timeIncrement: 10,
  },
  {
    hours: 4,
    index: 1,
    label: "4 hr",
    preset: {
      ...defaultPresetProperties(8),
      headers: [buildTopRowDayHeader(), buildMinuteRowHeader()],
      id: "4hr",
      level: 1,
      ticks: 8,
    },
    timeIncrement: 30,
  },
  {
    hours: 8,
    index: 2,
    label: "8 hr",
    preset: {
      ...defaultPresetProperties(16),
      headers: [buildTopRowDayHeader(), buildMinuteRowHeader()],
      id: "8hr",
      level: 2,
      ticks: 16,
    },
    timeIncrement: 30,
  },
  {
    hours: 12,
    index: 3,
    label: "12 hr",
    preset: {
      ...defaultPresetProperties(12),
      headers: [buildTopRowDayHeader(), buildHourRowHeader()],
      id: "12hr",
      level: 3,
      ticks: 12,
    },
    timeIncrement: 60,
  },
  {
    hours: 24,
    index: 4,
    label: "1 day",
    preset: {
      ...defaultPresetProperties(24),
      headers: [buildTopRowDayHeader(), buildHourRowHeader()],
      id: "1day",
      level: 4,
      ticks: 24,
    },
    timeIncrement: 60,
  },
  {
    hours: 72,
    index: 5,
    label: "3 days",
    preset: {
      ...defaultPresetProperties(24),
      headers: [buildTopRowDayHeader(), buildHourRowHeader({ increment: 3 })],
      id: "3days",
      level: 5,
      ticks: 24,
    },
    timeIncrement: 60,
  },
  {
    hours: 120,
    index: 6,
    label: "5 days",
    preset: {
      ...defaultPresetProperties(20),
      headers: [buildTopRowDayHeader(), buildHourRowHeader({ increment: 6 })],
      id: "5days",
      level: 6,
      ticks: 20,
    },
    timeIncrement: 60,
  },
  {
    hours: 168,
    index: 7,
    label: "1 week",
    preset: {
      ...defaultPresetProperties(28),
      headers: [buildTopRowDayHeader(), buildHourRowHeader({ increment: 6 })],
      id: "1week",
      level: 7,
      ticks: 28,
    },
    timeIncrement: 60,
  },
  {
    hours: 336,
    index: 8,
    label: "2 weeks",
    preset: {
      ...defaultPresetProperties(14),
      headers: [
        {
          dateFormat: "WWp",
          headerCellCls: "bryntumDateHeaderStyle",
          unit: "week",
        },
        buildSecondRowDayHeader(),
      ],
      id: "2weeks",
      level: 8,
      ticks: 14,
    },
    timeIncrement: 60,
  },
];

const maxZoomLevel = ZoomLevels.length - 1;

function zoomLevelMapping(zoomLevel) {
  return ZoomLevels[zoomLevel];
}

function zoomLevelDurationInHours(level) {
  return ZoomLevels[level]?.hours || ZoomLevels[0]?.hours;
}

function presetId(zoomLevel) {
  return zoomLevelMapping(zoomLevel)?.preset?.id || defaultViewPresetId;
}

function timeIncrement(zoomLevel) {
  return zoomLevelMapping(zoomLevel)?.timeIncrement || 60;
}

function calculateZoomOptions() {
  const options = map(ZoomLevels, (option, level) => ({ label: option.label, value: level }));

  return options.reverse();
}

const zoomOptions = calculateZoomOptions();

function calculateViewPresets() {
  return ZoomLevels.map((level) => level.preset).reverse();
}

const viewPresets = calculateViewPresets();

const defaultViewPresetId = "1week";

function calculateDefaultZoomLevel() {
  return ZoomLevels.findIndex((level) => level.preset.id === defaultViewPresetId);
}

const defaultZoomLevel = calculateDefaultZoomLevel();

export {
  DEFAULT_ZOOM_HOURS,
  defaultViewPresetId,
  defaultZoomLevel,
  getTickWidth,
  maxZoomLevel,
  presetId,
  timeIncrement,
  viewPresets,
  zoomLevelDurationInHours,
  zoomOptions,
};
